import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { captureReferralSource } from "../lib/referral";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";

// Free-license Unsplash photos (credit: Alexandra Bellanger, sofia inductgroup, Jonathan Borba).
// One is picked at random per page load so the hero panel cycles across visits.
const HERO_IMAGES = [
  "/images/auth-hero-canape.jpg",
  "/images/auth-hero-croissant.jpg",
  "/images/auth-hero-confectionery.jpg",
];

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [heroImage] = useState(() => HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)]);
  const [referralSource] = useState(captureReferralSource);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (mode === "register" && user === false) trackEvent("signup-started");
  }, [mode, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = mode === "login" ? { email: form.email, password: form.password } : { ...form, referral_source: referralSource };
      const { data } = await api.post(`/auth/${mode}`, payload);
      if (mode === "register") trackEvent("trial-started", { method: "email" });
      setUser(data);
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full h-12 px-4 rounded-lg border border-sand bg-white text-ink focus:outline-none focus:ring-2 focus:ring-tea-hover/70 transition-shadow duration-200";

  return (
    <div className="min-h-screen bg-cream flex flex-col md:flex-row font-body text-ink">
      <div
        className="md:w-1/2 bg-ink bg-cover bg-center text-white px-8 md:px-14 pb-8 md:pb-14 pt-[max(2rem,calc(env(safe-area-inset-top)+1rem))] md:pt-14 flex flex-col justify-between animate-rise"
        style={{ backgroundImage: `linear-gradient(180deg, rgba(20,18,15,0.55), rgba(20,18,15,0.8)), url(${heroImage})` }}
      >
        <div className="flex items-center gap-2">
          <Logo markSize={40} textSize="text-3xl" onDark />
        </div>

        <div className="py-10 md:py-0">
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight [text-wrap:balance]">
            Know exactly what to charge before you hit send.
          </h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-md [text-wrap:pretty]">
            Snap your receipts, build your recipes, and get a price floor for every quote. Then send the invoice and get paid by card, Klarna or Clearpay, straight into your own bank account.
          </p>

          {/* Real output from the live demo account, not a made-up stat. */}
          <div className="mt-8 max-w-sm rounded-2xl bg-white/95 backdrop-blur-sm p-5 text-ink shadow-[0_16px_40px_rgba(0,0,0,0.35)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink/40">The number that matters</p>
                <p className="font-heading font-bold text-lg mt-1">Butter chicken with rice</p>
                <p className="text-xs text-ink/50">80 guests</p>
              </div>
              <span className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-almond/20 text-almond">40% margin</span>
            </div>
            <div className="mt-4 pt-4 border-t border-sand flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-[0.14em] font-medium text-ink/50">Price floor</span>
              <span className="font-mono-num tabular-nums text-2xl font-semibold text-tea-text">£606.97</span>
            </div>
          </div>
        </div>

        <p className="hidden md:block text-white/40 text-sm">Built for the person doing both the cooking and the finance.</p>
      </div>

      <div className="md:w-1/2 flex items-center justify-center p-6 md:p-14">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4 animate-rise" data-testid="auth-form">
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl tracking-tight">
            {mode === "login" ? "Welcome back" : "Start your free trial"}
          </h2>
          <p className="text-sm text-ink/60">
            {mode === "login" ? "Log in to your costing workspace." : "No card required. Cost up to 2 real recipes free for 14 days, add a card any time before it ends."}
          </p>
          {mode === "register" && (
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/60">Your name</label>
              <input required data-testid="auth-name-input" className={`${field} mt-1`} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/60">Email</label>
            <input required type="email" data-testid="auth-email-input" className={`${field} mt-1`} value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-medium text-ink/60">Password</label>
            <input required type="password" data-testid="auth-password-input" className={`${field} mt-1`} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          {mode === "login" && (
            <div className="text-right -mt-2">
              <a href="/forgot-password" data-testid="auth-forgot-password-link" className="text-xs text-ink/50 hover:text-ink/70 hover:underline">
                Forgot password?
              </a>
            </div>
          )}
          <button disabled={busy} data-testid="auth-submit-button"
            className="w-full h-12 rounded-lg bg-tea hover:bg-tea-hover text-tea-text font-medium transition-[background-color,transform] duration-200 active:scale-[0.96] disabled:opacity-60 disabled:active:scale-100">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </button>
          <button type="button" data-testid="auth-toggle-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="w-full py-3 text-sm text-tea-text hover:underline">
            {mode === "login" ? "New here? Create an account" : "Already have an account? Log in"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-sand" />
            <span className="text-xs text-ink/40">or</span>
            <div className="flex-1 h-px bg-sand" />
          </div>
          <a href="/api/auth/google/login" data-testid="auth-google-button"
            className="w-full h-12 rounded-lg border border-sand bg-white hover:bg-sand/40 text-ink font-medium transition-[background-color,transform] duration-200 active:scale-[0.96] flex items-center justify-center gap-2">
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.68-3.87 2.68-6.62z" />
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z" />
              <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.33z" />
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.97l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z" />
            </svg>
            Continue with Google
          </a>

          <p className="text-xs text-ink/40 text-center [text-wrap:pretty]">
            By continuing, you agree to our{" "}
            <a href="/terms" className="underline hover:text-ink/60">Terms</a> and{" "}
            <a href="/privacy" className="underline hover:text-ink/60">Privacy Policy</a>. Need help?{" "}
            <a href="mailto:hello@mnandi.app" className="underline hover:text-ink/60">hello@mnandi.app</a>
          </p>
        </form>
      </div>
    </div>
  );
}
